import React, { useEffect, useRef, useState } from "react";
import apiRoutes from "../../api/apiRoutes";
import { apiFetchJson } from "../../utils/http";

const PAGE_SIZE = 20;
const DEBOUNCE_MS = 300;

export default function CiudadAutocomplete({
  value,
  onChange,
  disabled = false,
  placeholder = "Buscar ciudad...",
}) {
  const boxRef = useRef(null);
  const debounceRef = useRef(null);

  // ============================
  //        ESTADOS
  // ============================
  const [open, setOpen] = useState(false);
  const [inputText, setInputText] = useState("");
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);

  // ============================
  //   SINCRONIZAR VALUE → INPUT
  // ============================
  useEffect(() => {
    if (value && value.nombre) {
      setInputText(value.nombre);
    }
    if (!value) {
      setInputText("");
    }
  }, [value]);

  // ============================
  //   CLICK AFUERA → CERRAR
  // ============================
  useEffect(() => {
    const handleOutside = (event) => {
      if (boxRef.current && !boxRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, []);

  // ============================
  //        FETCH
  // ============================
  const fetchCiudades = async ({ pageIndex = 0, append = false, query }) => {
    if (loading) return;

    setLoading(true);

    const url = apiRoutes.ciudad.autocompleteUrl({
      q: query,
      page: pageIndex,
      size: PAGE_SIZE,
    });

    try {
      const response = await apiFetchJson(url, true);
      const responseItems = response?.items || [];

      const more =
        typeof response?.hasMore === "boolean"
          ? response.hasMore
          : responseItems.length === PAGE_SIZE;

      setItems((prev) =>
        append ? [...prev, ...responseItems] : responseItems
      );
      setHasMore(more);
      setPage(pageIndex);
    } catch {
      if (!append) {
        setItems([]);
        setHasMore(false);
      }
    } finally {
      setLoading(false);
    }
  };

  // ============================
  //        INPUT (debounce)
  // ============================
  const handleInputChange = (event) => {
    const valueText = event.target.value;
    setInputText(valueText);
    setOpen(true);

    // limpiar selección si se borra
    if (value && valueText.trim() === "") {
      onChange(null);
    }

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      fetchCiudades({
        pageIndex: 0,
        append: false,
        query: valueText.trim(),
      });
    }, DEBOUNCE_MS);
  };

  const handleFocus = () => {
    setOpen(true);

    // browse inicial
    if (items.length === 0 && !loading) {
      fetchCiudades({
        pageIndex: 0,
        append: false,
        query: inputText.trim(),
      });
    }
  };

  // ============================
  //     SCROLL INFINITO
  // ============================
  const handleScroll = (event) => {
    const el = event.currentTarget;
    const nearBottom =
      el.scrollTop + el.clientHeight >= el.scrollHeight - 40;

    if (nearBottom && hasMore && !loading) {
      fetchCiudades({
        pageIndex: page + 1,
        append: true,
        query: inputText.trim(),
      });
    }
  };

  // ============================
  //      SELECCIONAR
  // ============================
  const handleSelect = (ciudad) => {
    onChange(ciudad);
    setInputText(ciudad.nombre ?? "");
    setOpen(false);
  };

  // ============================
  //        LIMPIAR
  // ============================
  const clearSelection = () => {
    onChange(null);
    setInputText("");
    setItems([]);
    setOpen(false);
  };

  // ============================
  //        RENDER
  // ============================
  return (
    <div ref={boxRef} className="relative">
      <input
        type="text"
        className="input"
        disabled={disabled}
        placeholder={placeholder}
        value={inputText}
        onChange={handleInputChange}
        onFocus={handleFocus}
      />

      {value && (
        <button
          type="button"
          onClick={clearSelection}
          className="absolute right-3 top-2 text-gray-400"
          title="Limpiar"
        >
          ×
        </button>
      )}

      {open && (
        <div
          className="absolute z-20 mt-1 w-full rounded-md border border-gray-200 bg-white shadow-lg max-h-64 overflow-auto text-sm"
          onScroll={handleScroll}
        >
          {loading && items.length === 0 && (
            <div className="px-3 py-2 text-gray-500">
              Cargando ciudades...
            </div>
          )}

          {!loading && items.length === 0 && (
            <div className="px-3 py-2 text-gray-500">
              Sin resultados.
            </div>
          )}

          {items.map((ciudad) => (
            <button
              key={ciudad.id}
              type="button"
              className="block w-full text-left px-3 py-2 hover:bg-emerald-50"
              onClick={() => handleSelect(ciudad)}
            >
              {ciudad.codigo && (
                <div className="font-mono text-xs text-gray-700">
                  {ciudad.codigo}
                </div>
              )}

              <div className="text-sm font-medium">
                {ciudad.nombre}
              </div>

              {ciudad.nombrePais && (
                <div className="text-xs text-gray-500">
                  {ciudad.nombrePais}
                </div>
              )}
            </button>
          ))}

          {loading && items.length > 0 && (
            <div className="px-3 py-2 text-gray-400 text-xs">
              Cargando más…
            </div>
          )}
        </div>
      )}
    </div>
  );
}
